import { z } from 'zod';

/**
 * Structured output for the `playbookEditor` harness subagent.
 *
 * Follows the same structured-output constraints as
 * `redlines/redline-llm-output.ts`: plain `z.union` (a discriminated union
 * serializes to `oneOf`, which OpenAI rejects), `_type` first in every branch so
 * the constrained decoder pins the branch before any other key streams, every
 * field required (`.nullable()` where the model may have nothing to say), and no
 * `.min()` / `.max()` (OpenAI rejects `minItems`; Anthropic supports only 0 or
 * 1, and workloads fall back across providers). Invariants like "a rule keeps at
 * least one position" are enforced structurally (`startingPosition` is a
 * required field) or at accept time, never by array constraints.
 */

/**
 * A runaway guard for generated fallbacks, not a quality target. Native
 * structured outputs reject array bounds, so this is stated in the field
 * description and clamped again at persistence. High enough that a real ladder
 * of concessions still lands.
 */
export const MAX_GENERATED_FALLBACKS = 10;

/** A position as the model writes it, before it has an id or a rank. */
export const NewPositionSchema = z.strictObject({
  aiGuidance: z
    .string()
    .nullable()
    .describe(
      'How to attach a comment on this position for the counterparty or another reviewer: what to raise, why it matters, and for a fallback, when this weaker stance is acceptable (e.g. "Only accept New York if Delaware is refused."). Do not restate the requirement; that belongs in ruleText. Null when there is no guidance.'
    ),
  ruleText: z
    .string()
    .describe(
      'The requirement text for this position, stated as a standalone rule the document must satisfy.'
    ),
});
export type NewPosition = z.infer<typeof NewPositionSchema>;

/** A rule as the model writes it: a title, a starting position, and fallbacks. */
export const NewRuleSchema = z.strictObject({
  fallbackPositions: z
    .array(NewPositionSchema)
    .describe(
      `Acceptable concessions if the starting position is refused in negotiation, ordered from most to least preferred. May be empty. Include one only where a genuine, defensible concession exists — many rules have none. Never invent a weaker stance just to fill this in. Each fallback's ruleText must be a standalone requirement — the rule as it would read if the concession were adopted — never a condition for accepting it; acceptance conditions belong in that position's aiGuidance. At most ${MAX_GENERATED_FALLBACKS}.`
    ),
  startingPosition: NewPositionSchema.describe(
    'The preferred stance: the rule as the represented party would ideally have it. Every rule has exactly one.'
  ),
  title: z
    .string()
    .describe('A short name for the rule, 1-4 words (e.g., "Governing Law", "Payment Terms").'),
});
export type NewRule = z.infer<typeof NewRuleSchema>;

export const CreateCategorySuggestionSchema = z.object({
  _type: z.literal('create-category'),
  afterCategoryId: z
    .string()
    .nullable()
    .describe(
      'Id of the existing category this one goes directly after, or null for the start of the playbook. To append, use the last category id.'
    ),
  name: z.string().describe('The category name'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why this category is being created'),
  refId: z
    .string()
    .describe(
      "A short id you invent, e.g. 'new-category-1'. Later operations may use it wherever a category id is accepted."
    ),
  rules: z
    .array(NewRuleSchema)
    .describe('New rules to create inside the category, in order. May be empty.'),
});

export const EditCategorySuggestionSchema = z.object({
  _type: z.literal('edit-category'),
  categoryId: z.string().describe('The id of the category to edit'),
  name: z.string().describe('The new category name'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the category is being renamed'),
});

export const DeleteCategorySuggestionSchema = z.object({
  _type: z.literal('delete-category'),
  categoryId: z.string().describe('The id of the category to delete, along with every rule in it'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the category is being deleted'),
});

export const MoveCategorySuggestionSchema = z.object({
  _type: z.literal('move-category'),
  afterCategoryId: z
    .string()
    .nullable()
    .describe(
      'Id of the category this one lands directly after, or null for the start of the playbook'
    ),
  categoryId: z.string().describe('The id of the category to move'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the category is being moved'),
});

export const CreateRuleSuggestionSchema = z.object({
  _type: z.literal('create-rule'),
  afterRuleId: z
    .string()
    .nullable()
    .describe(
      'Id of the rule this one goes directly after, or null for the start of the category. To append, use the last rule id in the category.'
    ),
  categoryId: z
    .string()
    .describe(
      'The id of the category to create the rule in. May be a refId from an earlier create-category.'
    ),
  fallbackPositions: z
    .array(NewPositionSchema)
    .describe('Fallback positions in order from most to least preferred. May be empty.'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why this rule is being created'),
  refId: z
    .string()
    .describe(
      "A short id you invent, e.g. 'new-rule-1'. Later operations may use it wherever a rule id is accepted."
    ),
  startingPosition: NewPositionSchema.describe(
    'The preferred position. Every rule has exactly one.'
  ),
  title: z.string().describe('The rule title'),
});

export const EditRuleSuggestionSchema = z.object({
  _type: z.literal('edit-rule'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the rule is being retitled'),
  ruleId: z.string().describe('The id of the rule to edit'),
  title: z.string().describe('The new rule title'),
});

export const DeleteRuleSuggestionSchema = z.object({
  _type: z.literal('delete-rule'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the rule is being deleted'),
  ruleId: z.string().describe('The id of the rule to delete, along with all of its positions'),
});

export const MoveRuleSuggestionSchema = z.object({
  _type: z.literal('move-rule'),
  afterRuleId: z
    .string()
    .nullable()
    .describe(
      'Id of the rule this one lands directly after, or null for the start of the destination category'
    ),
  categoryId: z
    .string()
    .describe(
      'The id of the destination category. May be the current category (a reorder) or a different one. May be a refId.'
    ),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the rule is being moved'),
  ruleId: z.string().describe('The id of the rule to move'),
});

export const CreatePositionSuggestionSchema = z.object({
  _type: z.literal('create-position'),
  afterPositionId: z
    .string()
    .nullable()
    .describe(
      'Id of the position this one goes directly after, or null to make it the new starting position. To append, use the last position id in the rule.'
    ),
  aiGuidance: z
    .string()
    .nullable()
    .describe('Guidance for AI-generated comments, or null for none'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why this position is being added'),
  refId: z
    .string()
    .describe(
      "A short id you invent, e.g. 'new-position-1'. Later operations may use it wherever a position id is accepted."
    ),
  ruleId: z
    .string()
    .describe(
      'The id of the rule to add the position to. May be a refId from an earlier create-rule.'
    ),
  ruleText: z.string().describe('The standalone requirement text for this position'),
});

export const EditPositionSuggestionSchema = z.object({
  _type: z.literal('edit-position'),
  aiGuidance: z
    .string()
    .nullable()
    .describe(
      'The new comment guidance, null to leave it unchanged, or an empty string to clear it'
    ),
  playbookId: z.string().describe('The id of the playbook'),
  positionId: z.string().describe('The id of the position to edit'),
  rationale: z.string().describe('Why the position is being edited'),
  ruleId: z.string().describe('The id of the rule that contains the position'),
  ruleText: z
    .string()
    .nullable()
    .describe('The new requirement text, or null to leave it unchanged'),
});

export const DeletePositionSuggestionSchema = z.object({
  _type: z.literal('delete-position'),
  playbookId: z.string().describe('The id of the playbook'),
  positionId: z.string().describe('The id of the position to delete'),
  rationale: z.string().describe('Why the position is being deleted'),
  ruleId: z.string().describe('The id of the rule that contains the position'),
});

export const MovePositionSuggestionSchema = z.object({
  _type: z.literal('move-position'),
  afterPositionId: z
    .string()
    .nullable()
    .describe(
      'Id of the position this one lands directly after, or null to make it the starting position'
    ),
  playbookId: z.string().describe('The id of the playbook'),
  positionId: z.string().describe('The id of the position to move'),
  rationale: z.string().describe('Why the position is being moved'),
  ruleId: z
    .string()
    .describe('The id of the rule that contains the position. Positions never move between rules.'),
});

export const EditPlaybookSuggestionSchema = z.object({
  _type: z.literal('edit-playbook'),
  description: z
    .string()
    .nullable()
    .describe('The new playbook description, or null to leave it unchanged'),
  playbookId: z.string().describe('The id of the playbook'),
  rationale: z.string().describe('Why the playbook properties are being edited'),
  title: z.string().nullable().describe('The new playbook title, or null to leave it unchanged'),
});

export const PlaybookNoteSuggestionSchema = z.object({
  _type: z.literal('note'),
  playbookId: z.string().describe('The id of the playbook the note is about'),
  text: z
    .string()
    .describe(
      'User-facing markdown: the answer to a question about the playbook, or why an edit was not made'
    ),
});

export const PlaybookEditSchema = z.object({
  suggest: z.union([
    CreateCategorySuggestionSchema,
    EditCategorySuggestionSchema,
    DeleteCategorySuggestionSchema,
    MoveCategorySuggestionSchema,
    CreateRuleSuggestionSchema,
    EditRuleSuggestionSchema,
    DeleteRuleSuggestionSchema,
    MoveRuleSuggestionSchema,
    CreatePositionSuggestionSchema,
    EditPositionSuggestionSchema,
    DeletePositionSuggestionSchema,
    MovePositionSuggestionSchema,
    EditPlaybookSuggestionSchema,
    PlaybookNoteSuggestionSchema,
  ]),
});

export type CreateCategorySuggestion = z.infer<typeof CreateCategorySuggestionSchema>;
export type EditCategorySuggestion = z.infer<typeof EditCategorySuggestionSchema>;
export type DeleteCategorySuggestion = z.infer<typeof DeleteCategorySuggestionSchema>;
export type MoveCategorySuggestion = z.infer<typeof MoveCategorySuggestionSchema>;
export type CreateRuleSuggestion = z.infer<typeof CreateRuleSuggestionSchema>;
export type EditRuleSuggestion = z.infer<typeof EditRuleSuggestionSchema>;
export type DeleteRuleSuggestion = z.infer<typeof DeleteRuleSuggestionSchema>;
export type MoveRuleSuggestion = z.infer<typeof MoveRuleSuggestionSchema>;
export type CreatePositionSuggestion = z.infer<typeof CreatePositionSuggestionSchema>;
export type EditPositionSuggestion = z.infer<typeof EditPositionSuggestionSchema>;
export type DeletePositionSuggestion = z.infer<typeof DeletePositionSuggestionSchema>;
export type MovePositionSuggestion = z.infer<typeof MovePositionSuggestionSchema>;
export type EditPlaybookSuggestion = z.infer<typeof EditPlaybookSuggestionSchema>;
export type PlaybookNoteSuggestion = z.infer<typeof PlaybookNoteSuggestionSchema>;
export type PlaybookEdit = z.infer<typeof PlaybookEditSchema>;

/** One op: what the model emits inside each `suggest`. Ops are transient; suggested edits persist. */
export type PlaybookEditOp = PlaybookEdit['suggest'];
export type PlaybookEditOpType = PlaybookEditOp['_type'];
